﻿// 真实AI服务模块 - 使用配置的API进行实际调用
import settingsService from './settingsService.js'
import { AdvancedJsonRepair } from '../utils/advancedJsonRepair.js'
import { DoubaoImageServiceV2 } from './doubaoImageServiceV2.js'

export class RealAIService {
  // 调用语言大模型生成内容
  // prompt可以是字符串或messages数组 [{role: 'system'|'user'|'assistant', content: string}]
  static async callLanguageModel(prompt, options = {}) {
    const config = settingsService.getLanguageModelConfig()
    
    if (!config.apiKey || !config.apiUrl) {
      throw new Error('语言模型API未配置')
    }

    // 检查网络连接状态
    if (!navigator.onLine) {
      throw new Error('网络连接已断开，请检查网络连接')
    }

    // 判断prompt是字符串还是messages数组
    const isMessagesArray = Array.isArray(prompt)
    const messages = isMessagesArray ? prompt : null
    const promptText = isMessagesArray ? null : prompt

    // 优化重试机制 - 减少重试次数提高响应速度
    const maxRetries = 2  // 减少到2次重试
    let lastError = null
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        console.log(`🔄 API调用尝试 ${attempt}/${maxRetries}`)
        console.log(`📤 发送请求到: ${config.apiUrl}`)
        if (isMessagesArray) {
          console.log(`📝 消息数量: ${messages.length} 条`)
        } else {
          console.log(`📝 提示词长度: ${promptText.length} 字符`)
        }
        console.log(`⚙️ 配置参数:`, { maxTokens: options.maxTokens || 8192, temperature: options.temperature || 0.7 })
        
        // 每次重试前检查网络状态
        if (!navigator.onLine) {
          throw new Error('网络连接已断开')
        }
        
        const startTime = Date.now()
        const result = await this.makeApiCall(config, promptText || messages, options, isMessagesArray)
        const endTime = Date.now()
        console.log(`✅ API调用成功，耗时: ${endTime - startTime}ms`)
        return result
      } catch (error) {
        lastError = error
        console.warn(`⚠️ API调用尝试 ${attempt} 失败:`, error.message)
        
        if (attempt < maxRetries) {
          // 等待一段时间后重试，使用更快的重试策略
          const delay = Math.pow(1.5, attempt) * 1000 // 快速退避：1.5s, 2.25s, 3.375s
          console.log(`⏳ 等待 ${delay}ms 后重试...`)
          await new Promise(resolve => setTimeout(resolve, delay))
        }
      }
    }
    
    // 所有重试都失败了
    throw lastError
  }

  // 执行实际的API调用
  // input可以是字符串或messages数组
  static async makeApiCall(config, input, options = {}, isMessagesArray = false) {
    // 将input作为参数保存，避免在错误处理中丢失
    const originalInput = input
    let requestBody
    let headers = {
      'Content-Type': 'application/json'
    }

    // 根据模型类型构建不同的请求体
    if (config.model.includes('gemini')) {
      // Gemini API格式 - 使用X-Goog-Api-Key头部
      headers['X-Goog-Api-Key'] = config.apiKey
      
      if (isMessagesArray && Array.isArray(input)) {
        // 转换messages数组为Gemini格式
        const contents = input.map(msg => ({
          role: msg.role === 'system' ? 'user' : msg.role === 'assistant' ? 'model' : 'user',
          parts: [{ text: msg.content }]
        }))
        requestBody = {
          contents,
          generationConfig: {
            temperature: options.temperature || 0.7,
            maxOutputTokens: options.maxTokens || 6144,
            topP: options.topP || 0.8,
            topK: options.topK || 40
          }
        }
      } else {
        // 字符串格式
        requestBody = {
          contents: [{
            parts: [{
              text: input
            }]
          }],
          generationConfig: {
            temperature: options.temperature || 0.7,
            maxOutputTokens: options.maxTokens || 6144,
            topP: options.topP || 0.8,
            topK: options.topK || 40
          }
        }
      }
    } else if (config.model.includes('deepseek')) {
      // DeepSeek API格式
      headers['Authorization'] = `Bearer ${config.apiKey}`
      
      if (isMessagesArray && Array.isArray(input)) {
        // 使用messages数组
        requestBody = {
          model: config.model,
          messages: input,
          temperature: options.temperature || 0.7,
          max_tokens: options.maxTokens || 6144,
          stream: options.stream !== undefined ? options.stream : true
        }
      } else {
        // 字符串格式
        requestBody = {
          model: config.model,
          messages: [
            { role: 'user', content: input }
          ],
          temperature: options.temperature || 0.7,
          max_tokens: options.maxTokens || 6144,
          stream: options.stream !== undefined ? options.stream : true
        }
      }
    } else {
      // 通用OpenAI格式
      headers['Authorization'] = `Bearer ${config.apiKey}`
      
      if (isMessagesArray && Array.isArray(input)) {
        // 使用messages数组
        requestBody = {
          model: config.model,
          messages: input,
          temperature: options.temperature || 0.7,
          max_tokens: options.maxTokens || 6144
        }
      } else {
        // 字符串格式
        requestBody = {
          model: config.model,
          messages: [
            { role: 'user', content: input }
          ],
          temperature: options.temperature || 0.7,
          max_tokens: options.maxTokens || 6144
        }
      }
    }

    // 添加超时控制 - 进一步缩短超时时间提高响应性
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 60000) // 60秒超时，给流式响应更多时间

    try {
      console.log(`🚀 开始发送API请求...`)
      console.log(`📡 请求头:`, headers)
      console.log(`📦 请求体:`, { ...requestBody, messages: requestBody.messages ? `[${requestBody.messages.length}条消息]` : '无' })
      
      const response = await fetch(config.apiUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify(requestBody),
        signal: controller.signal
      })
      
      console.log(`📨 收到响应状态: ${response.status} ${response.statusText}`)

      clearTimeout(timeoutId)

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(`API调用失败: ${response.status} ${errorData.error?.message || response.statusText}`)
      }

      // 检查是否是流式响应
      const contentType = response.headers.get('content-type') || ''
      const isStreaming = requestBody.stream && (
        contentType.includes('text/plain') || 
        contentType.includes('text/event-stream') ||
        contentType.includes('application/x-ndjson') ||
        contentType.includes('text/stream')
      )
      
      if (isStreaming) {
        console.log('📡 使用流式响应模式...')
        try {
          return await this.handleStreamResponse(response, config)
        } catch (streamError) {
          console.warn('⚠️ 流式响应处理失败，回退到标准模式:', streamError.message)
          // 直接尝试解析为JSON响应
          try {
            const data = await response.json()
            if (config.model.includes('gemini')) {
              return data.candidates?.[0]?.content?.parts?.[0]?.text || '无响应内容'
            } else {
              return data.choices?.[0]?.message?.content || '无响应内容'
            }
          } catch (jsonError) {
            throw new Error('流式和标准响应解析都失败了')
          }
        }
      } else {
        console.log('📡 使用标准响应模式...')
        const data = await response.json()
        
        // 根据不同的API响应格式提取文本
        if (config.model.includes('gemini')) {
          return data.candidates?.[0]?.content?.parts?.[0]?.text || '无响应内容'
        } else {
          return data.choices?.[0]?.message?.content || '无响应内容'
        }
      }
    } catch (error) {
      clearTimeout(timeoutId)
      if (error.name === 'AbortError') {
        throw new Error('API调用超时，请稍后重试')
      }
      throw error
    }
  }

  // 处理流式响应
  static async handleStreamResponse(response, config) {
    console.log('🌊 开始处理流式响应...')
    
    try {
      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let fullContent = ''
      let buffer = ''

      while (true) {
        const { done, value } = await reader.read()
        
        if (done) {
          break
        }

        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() // 保留最后不完整的行

        for (const line of lines) {
          if (line.trim() === '') continue
          if (line.startsWith('data: ')) {
            const data = line.slice(6)
            if (data === '[DONE]') {
              break
            }
            
            try {
              // 跳过空数据和结束标记
              if (data.trim() === '' || data.trim() === '[DONE]') {
                continue
              }
              
              const parsed = JSON.parse(data)
              let content = ''
              
              if (config.model.includes('deepseek')) {
                content = parsed.choices?.[0]?.delta?.content || ''
              } else if (config.model.includes('gemini')) {
                content = parsed.candidates?.[0]?.content?.parts?.[0]?.text || ''
              } else {
                content = parsed.choices?.[0]?.delta?.content || ''
              }
              
              if (content) {
                fullContent += content
                // 这里可以添加实时更新UI的逻辑
                if (fullContent.length % 100 === 0) { // 每100字符打印一次状态
                  console.log('📝 接收流式内容，当前长度:', fullContent.length)
                }
              }
            } catch (parseError) {
              console.warn('⚠️ 解析流式数据失败:', parseError.message, '原始数据:', data.substring(0, 50))
              // 对于无法解析的数据，尝试直接提取content字段
              const contentMatch = data.match(/"content"\s*:\s*"([^"]*)"/)
              if (contentMatch && contentMatch[1]) {
                fullContent += contentMatch[1]
              }
            }
          }
        }
      }

      console.log('✅ 流式响应处理完成，总长度:', fullContent.length)
      return fullContent
    } catch (error) {
      console.error('❌ 流式响应处理失败:', error)
      throw error
    }
  }

  // 调用图像生成模型
  static async generateImage(prompt, options = {}) {
    const config = settingsService.getImageGenerationConfig()
    
    if (!config.apiKey || !config.apiUrl) {
      throw new Error('图像生成API未配置')
    }

    try {
      if (config.model === 'comfyui-local') {
        return await this.generateImageWithComfyUI(prompt, options)
      } else if (config.model.includes('gemini')) {
        return await this.generateImageWithGemini(prompt, options)
      } else if (config.model === 'doubao-seedream-4-0') {
        return await this.generateImageWithDoubao(prompt, options)
      } else {
        throw new Error(`不支持的图像生成模型: ${config.model}`)
      }
    } catch (error) {
      console.error('图像生成API调用失败:', error)
      throw error
    }
  }

  // 使用Gemini生成图像
  static async generateImageWithGemini(prompt, options = {}) {
    const config = settingsService.getImageGenerationConfig()
    
         const response = await fetch(config.apiUrl, {
       method: 'POST',
       headers: {
         'Content-Type': 'application/json',
         'X-Goog-Api-Key': config.apiKey
       },
      body: JSON.stringify({
        contents: [{
          parts: [{
            text: prompt
          }]
        }],
        generationConfig: {
          temperature: options.temperature || 0.4,
          maxOutputTokens: options.maxTokens || 8192  // 图像生成也增加token限制
        }
      })
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(`Gemini图像生成失败: ${response.status} ${errorData.error?.message || response.statusText}`)
    }

    const data = await response.json()
    
    // Gemini图像生成返回的是base64编码的图像数据
    const imageData = data.candidates?.[0]?.content?.parts?.[0]?.inlineData?.data
    if (imageData) {
      return `data:image/png;base64,${imageData}`
    } else {
      throw new Error('Gemini未返回图像数据')
    }
  }

  // 使用豆包生成图像
  static async generateImageWithDoubao(prompt, options = {}) {
    try {
      console.log('🎨 使用豆包API生成图像:', prompt.substring(0, 50) + '...')
      
      // 调用豆包图像服务
      const imageUrl = await DoubaoImageServiceV2.generateImage(prompt, {
        maxImages: 1,
        size: options.size || '2K',
        watermark: options.watermark !== undefined ? options.watermark : true
      })
      
      console.log('✅ 豆包图像生成成功:', imageUrl)
      return imageUrl
    } catch (error) {
      console.error('❌ 豆包图像生成失败:', error)
      throw error
    }
  }

  // 使用ComfyUI生成图像
  static async generateImageWithComfyUI(prompt, options = {}) {
    const config = settingsService.getImageGenerationConfig()
    
    // ComfyUI需要特定的工作流格式
    const workflow = {
      "3": {
        "inputs": {
          "seed": Math.floor(Math.random() * 1000000),
          "steps": options.steps || 20,
          "cfg": options.cfg || 7.0,
          "sampler_name": options.sampler || "euler",
          "scheduler": "normal",
          "denoise": 1.0,
          "model": ["4", 0],
          "positive": ["6", 0],
          "negative": ["7", 0],
          "latent_image": ["5", 0]
        },
        "class_type": "KSampler",
        "_meta": {
          "title": "KSampler"
        }
      },
      "4": {
        "inputs": {
          "ckpt_name": options.model || "v1-5-pruned-emaonly.ckpt"
        },
        "class_type": "CheckpointLoaderSimple",
        "_meta": {
          "title": "Load Checkpoint"
        }
      },
      "5": {
        "inputs": {
          "width": options.width || 512,
          "height": options.height || 512,
          "batch_size": 1
        },
        "class_type": "EmptyLatentImage",
        "_meta": {
          "title": "Empty Latent Image"
        }
      },
      "6": {
        "inputs": {
          "text": prompt,
          "clip": ["4", 1]
        },
        "class_type": "CLIPTextEncode",
        "_meta": {
          "title": "CLIP Text Encode (Prompt)"
        }
      },
      "7": {
        "inputs": {
          "text": options.negativePrompt || "blurry, low quality, distorted",
          "clip": ["4", 1]
        },
        "class_type": "CLIPTextEncode",
        "_meta": {
          "title": "CLIP Text Encode (Prompt)"
        }
      },
      "8": {
        "inputs": {
          "samples": ["3", 0],
          "vae": ["4", 2]
        },
        "class_type": "VAEDecode",
        "_meta": {
          "title": "VAE Decode"
        }
      },
      "9": {
        "inputs": {
          "filename_prefix": "ComfyUI",
          "images": ["8", 0]
        },
        "class_type": "SaveImage",
        "_meta": {
          "title": "Save Image"
        }
      }
    }

    // 提交工作流到ComfyUI
    const response = await fetch(config.apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        prompt: workflow,
        client_id: `altverse_${Date.now()}`
      })
    })

    if (!response.ok) {
      throw new Error(`ComfyUI调用失败: ${response.status}`)
    }

    const result = await response.json()
    const promptId = result.prompt_id

    // 等待ComfyUI处理完成
    return await this.waitForComfyUIResult(promptId, config.apiUrl)
  }

  // 等待ComfyUI处理完成并获取结果
  static async waitForComfyUIResult(promptId, baseUrl, maxWaitTime = 60000) {
    const startTime = Date.now()
    
    while (Date.now() - startTime < maxWaitTime) {
      try {
        const response = await fetch(`${baseUrl.replace('/prompt', '/history')}/${promptId}`)
        if (response.ok) {
          const data = await response.json()
          if (data[promptId] && data[promptId].status?.status_str === 'success') {
            // 获取生成的图像
            const outputs = data[promptId].outputs
            if (outputs && outputs['9'] && outputs['9'].images && outputs['9'].images.length > 0) {
              const imageInfo = outputs['9'].images[0]
              // 返回ComfyUI生成的图像URL
              return `${baseUrl.replace('/prompt', '')}/view?filename=${imageInfo.filename}&subfolder=${imageInfo.subfolder}&type=${imageInfo.type}`
            }
          } else if (data[promptId] && data[promptId].status?.status_str === 'error') {
            throw new Error('ComfyUI处理失败')
          }
        }
        
        // 等待2秒后重试
        await new Promise(resolve => setTimeout(resolve, 2000))
      } catch (error) {
        console.error('等待ComfyUI结果时出错:', error)
        await new Promise(resolve => setTimeout(resolve, 2000))
      }
    }
    
    throw new Error('ComfyUI处理超时')
  }

  // 生成故事大纲 - 使用真实API
  static async generateStoryOutline(content, genre, style) {
    const prompt = `请基于以下内容创建一个${genre}风格的互动故事大纲：

内容：${content}

要求：
1. 生成一个吸引人的标题
2. 创建故事摘要
3. 设计3-5个主要章节
4. 创建3-5个主要角色
5. 设计主要剧情线
6. 创建2-3个分支选择点

请以JSON格式返回，包含title, summary, chapters, characters, mainPlot, branches字段。`

    const response = await this.callLanguageModel(prompt, {
      temperature: 0.8,
      maxTokens: 2048
    })

    try {
      console.log('🔍 AI返回的原始响应:', response.substring(0, 500) + '...')
      
      // 尝试多种JSON提取方法
      let jsonText = null
      
      // 方法1: 查找第一个完整的JSON对象
      const jsonMatch = response.match(/\{[\s\S]*?\}(?=\s*$|\s*\{|\s*```)/)
      if (jsonMatch) {
        jsonText = jsonMatch[0]
        console.log('📝 方法1提取的JSON:', jsonText.substring(0, 200) + '...')
      }
      
      // 方法2: 查找```json代码块
      if (!jsonText) {
        const codeBlockMatch = response.match(/```json\s*([\s\S]*?)\s*```/)
        if (codeBlockMatch) {
          jsonText = codeBlockMatch[1]
          console.log('📝 方法2提取的JSON:', jsonText.substring(0, 200) + '...')
        }
      }
      
      // 方法3: 查找```代码块
      if (!jsonText) {
        const codeBlockMatch = response.match(/```\s*([\s\S]*?)\s*```/)
        if (codeBlockMatch) {
          jsonText = codeBlockMatch[1]
          console.log('📝 方法3提取的JSON:', jsonText.substring(0, 200) + '...')
        }
      }
      
      if (jsonText) {
        // 使用高级JSON修复工具
        const parsed = AdvancedJsonRepair.repairAndParse(jsonText)
        if (parsed) {
          console.log('✅ 高级JSON修复和解析成功')
          return parsed
        }
        
        // 如果高级修复失败，尝试原始方法
        try {
          const parsed = JSON.parse(jsonText)
          console.log('✅ 直接JSON解析成功')
          return parsed
        } catch (parseError) {
          console.error('❌ 直接JSON解析失败:', parseError)
          console.log('📄 尝试解析的JSON文本:', jsonText.substring(0, 500))
        }
      }
      
      // 如果所有JSON解析都失败，返回结构化数据
      console.log('⚠️ 无法解析JSON，使用文本提取方法')
      return {
        title: this.extractTitle(response),
        summary: this.extractSummary(response),
        chapters: this.extractChapters(response),
        characters: this.extractCharacters(response),
        mainPlot: this.extractMainPlot(response),
        branches: this.extractBranches(response),
        genre,
        style
      }
    } catch (error) {
      console.error('解析故事大纲失败:', error)
      // 返回默认结构
      return {
        title: "AI生成的故事",
        summary: response.substring(0, 200) + "...",
        chapters: [{ id: 1, title: "第一章", description: "故事开始", scenes: 3 }],
        characters: [{ name: "主角", role: "protagonist", description: "故事的主要角色" }],
        mainPlot: response.substring(0, 300) + "...",
        branches: [],
        genre,
        style
      }
    }
  }



  // 生成场景图像
  static async generateSceneImage(sceneDescription, characters, style) {
    const characterNames = characters ? characters.map(c => c.name).join(', ') : ''
    const prompt = `${sceneDescription} ${characterNames ? `featuring characters: ${characterNames}` : ''} in ${style} style, high quality, cinematic composition, dramatic lighting`
    
    return await this.generateImage(prompt, {
      temperature: 0.4,
      width: 512,
      height: 512
    })
  }

  // 批量生成所有场景内容 - 减少API调用次数
  static async generateAllScenesContent(storyStructure, characters, style) {
    console.log('🔄 批量生成场景内容，减少API调用次数...')
    
    const allScenes = []
    const characterNames = characters ? characters.map(c => c.name).join('、') : '主角'
    
    // 构建批量生成的prompt
    const scenesInfo = []
    for (const chapter of storyStructure.chapters) {
      for (const scene of chapter.scenes) {
        scenesInfo.push({
          id: scene.id,
          title: scene.title,
          description: scene.description || '',
          chapter: chapter.id,
          scene: scene.scene || 1
        })
      }
    }
    
    const prompt = `请为以下场景批量生成详细的互动内容：

故事标题：${storyStructure.title}
角色：${characterNames}
风格：${style}

场景列表：
${scenesInfo.map(s => `- ${s.id}: ${s.title} (${s.description})`).join('\n')}

要求：
1. 为每个场景生成500-800字的详细内容
2. 包含环境描述、角色互动、情感氛围
3. 为有选择分支的场景生成对话和选择
4. 包含丰富的感官细节

**重要：请严格按照以下格式输出，使用明确的标记来标识JSON内容：**

===JSON_START===
{
  "scenes": [
    {
      "id": "scene-1-1",
      "title": "场景标题",
      "content": "详细场景内容...",
      "description": "场景描述",
      "dialogues": [{"character": "角色名", "content": "对话内容"}],
      "background": "背景描述",
      "mood": "紧张",
      "timeOfDay": "下午",
      "location": "地点",
      "choices": [
        {
          "id": "choice-1-1-1",
          "text": "选择文本",
          "targetNodeId": "scene-1-2",
          "path": "brave"
        }
      ]
    }
  ]
}
===JSON_END===

请确保：
1. JSON内容完全在 ===JSON_START=== 和 ===JSON_END=== 之间
2. JSON格式完全正确，包含所有必需字段
3. 中文字符不需要特殊处理，直接输出即可
4. 每个场景的choices数组包含完整的选择信息`

    try {
      const response = await this.callLanguageModel(prompt, {
        temperature: 0.8,
        maxTokens: 4096
      })

      // 解析JSON响应 - 使用标记直接截取
      console.log('🔍 原始AI响应长度:', response.length)
      console.log('🔍 原始AI响应前500字符:', response.substring(0, 500))
      
      let jsonData = null
      let jsonString = null
      
      // 方法1: 使用标记直接截取JSON内容
      const startMarker = '===JSON_START==='
      const endMarker = '===JSON_END==='
      
      const startIndex = response.indexOf(startMarker)
      const endIndex = response.indexOf(endMarker)
      
      if (startIndex !== -1 && endIndex !== -1 && endIndex > startIndex) {
        jsonString = response.substring(startIndex + startMarker.length, endIndex).trim()
        console.log('✅ 使用标记成功提取JSON内容')
        console.log('🔍 提取的JSON字符串长度:', jsonString.length)
        console.log('🔍 提取的JSON字符串前200字符:', jsonString.substring(0, 200))
      } else {
        console.warn('⚠️ 未找到JSON标记，尝试传统方法')
        
        // 方法2: 查找完整的JSON对象（备用方案）
        const jsonMatch = response.match(/\{[\s\S]*\}/)
        if (jsonMatch) {
          jsonString = jsonMatch[0]
          console.log('🔍 使用传统方法提取JSON')
        }
        
        // 方法3: 查找scenes数组（备用方案）
        if (!jsonString) {
          const scenesMatch = response.match(/"scenes"\s*:\s*\[[\s\S]*?\]/)
          if (scenesMatch) {
            jsonString = `{"scenes": ${scenesMatch[0].replace(/^"scenes"\s*:\s*/, '')}}`
            console.log('🔍 使用scenes数组提取方法')
          }
        }
      }
      
      if (jsonString) {
        try {
          // 直接解析JSON，不做复杂处理
          console.log('🔍 直接解析JSON内容...')
          jsonData = JSON.parse(jsonString)
          console.log('✅ JSON解析成功')
        } catch (parseError) {
          console.error('❌ JSON解析失败:', parseError.message)
          console.log('🔍 解析失败的JSON位置:', parseError.message.match(/position (\d+)/)?.[1])
          
          // 简单的修复尝试：只处理最常见的错误
          try {
            console.log('🔧 尝试简单修复...')
            let fixedJson = jsonString
              .replace(/,(\s*[}\]])/g, '$1')  // 移除尾随逗号
              .replace(/\n/g, '\\n')         // 转义换行符
              .replace(/\r/g, '\\r')         // 转义回车符
              .replace(/\t/g, '\\t')         // 转义制表符
              
            console.log('🔧 修复后的JSON前200字符:', fixedJson.substring(0, 200))
            jsonData = JSON.parse(fixedJson)
            console.log('✅ JSON简单修复成功')
          } catch (fixError) {
            console.error('❌ JSON简单修复也失败:', fixError.message)
            
            // 最后的备用方案：尝试提取部分有效数据
            try {
              console.log('🔧 尝试提取部分有效数据...')
              const fallbackData = this.extractPartialData(jsonString)
              if (fallbackData && fallbackData.scenes && fallbackData.scenes.length > 0) {
                jsonData = fallbackData
                console.log('✅ 部分数据提取成功')
              } else {
                throw new Error('无法提取有效数据')
              }
            } catch (extractError) {
              console.error('❌ 数据提取也失败:', extractError.message)
              throw new Error(`JSON解析失败: ${parseError.message}`)
            }
          }
        }
      } else {
        throw new Error('无法从响应中提取JSON数据')
      }
      
      if (jsonData && jsonData.scenes && Array.isArray(jsonData.scenes)) {
        // 将生成的内容合并到原始场景中
        for (const chapter of storyStructure.chapters) {
          for (const scene of chapter.scenes) {
            const generatedScene = jsonData.scenes.find(s => s.id === scene.id)
            if (generatedScene) {
              allScenes.push({
                ...scene,
                content: generatedScene.content || scene.content || `在${scene.title}中，${characterNames}面临着重要的选择。`,
                description: generatedScene.description || scene.description || `这是${scene.title}的详细描述。`,
                dialogues: generatedScene.dialogues || scene.dialogues || [],
                background: generatedScene.background || scene.background || `在${scene.title}的背景中，环境氛围紧张而充满悬念。`,
                mood: generatedScene.mood || scene.mood || '紧张',
                timeOfDay: generatedScene.timeOfDay || scene.timeOfDay || '下午',
                location: generatedScene.location || scene.location || `在${scene.title}的场景中`
              })
            } else {
              // 如果没有生成的内容，使用原始场景
              allScenes.push(scene)
            }
          }
        }
      } else {
        console.warn('⚠️ 响应格式不正确，使用原始场景数据')
        // 使用原始场景数据作为备用方案
        for (const chapter of storyStructure.chapters) {
          for (const scene of chapter.scenes) {
            allScenes.push({
              ...scene,
              content: scene.content || `在${scene.title}中，${characterNames}面临着重要的选择。`,
              description: scene.description || `这是${scene.title}的详细描述。`,
              dialogues: scene.dialogues || [],
              background: scene.background || `在${scene.title}的背景中，环境氛围紧张而充满悬念。`,
              mood: scene.mood || '紧张',
              timeOfDay: scene.timeOfDay || '下午',
              location: scene.location || `在${scene.title}的场景中`
            })
          }
        }
      }
      
      // 如果仍然没有场景数据，创建基本的场景结构
      if (allScenes.length === 0) {
        console.warn('⚠️ 没有找到任何场景数据，创建基本场景结构')
        for (let i = 1; i <= 3; i++) {
          allScenes.push({
            id: `scene-1-${i}`,
            title: `第${i}个场景`,
            content: `这是第${i}个场景的内容。在${storyStructure.title}中，${characterNames}面临着重要的选择。`,
            description: `第${i}个场景的详细描述`,
            dialogues: [],
            background: `第${i}个场景的背景环境`,
            mood: '紧张',
            timeOfDay: '下午',
            location: `第${i}个场景的地点`,
            chapter: 1,
            scene: i,
            choices: i === 2 ? [
              {
                id: `choice-1-${i}-1`,
                text: '勇敢面对',
                targetNodeId: `scene-1-${i + 1}`,
                path: 'brave'
              },
              {
                id: `choice-1-${i}-2`,
                text: '谨慎行事',
                targetNodeId: `scene-1-${i + 1}`,
                path: 'cautious'
              }
            ] : []
          })
        }
      }
      
      console.log('✅ 批量场景内容生成成功，场景数:', allScenes.length)
      return allScenes
    } catch (error) {
      console.error('❌ 批量场景内容生成失败:', error)
      throw error
    }
  }

  // 提取部分有效数据 - 当JSON解析完全失败时的备用方案
  static extractPartialData(jsonString) {
    console.log('🔧 开始提取部分有效数据...')
    
    try {
      // 尝试提取scenes数组
      const scenesMatch = jsonString.match(/"scenes"\s*:\s*\[([\s\S]*?)\]/)
      if (scenesMatch) {
        const scenesContent = scenesMatch[1]
        console.log('🔍 找到scenes内容，长度:', scenesContent.length)
        
        // 尝试提取每个场景的基本信息
        const sceneMatches = scenesContent.match(/\{[^{}]*"id"[^{}]*\}/g)
        if (sceneMatches && sceneMatches.length > 0) {
          console.log('🔍 找到场景数量:', sceneMatches.length)
          
          const scenes = sceneMatches.map((sceneStr, index) => {
            try {
              // 提取基本信息
              const idMatch = sceneStr.match(/"id"\s*:\s*"([^"]+)"/)
              const titleMatch = sceneStr.match(/"title"\s*:\s*"([^"]+)"/)
              const contentMatch = sceneStr.match(/"content"\s*:\s*"([^"]+)"/)
              
              return {
                id: idMatch ? idMatch[1] : `scene-1-${index + 1}`,
                title: titleMatch ? titleMatch[1] : `场景${index + 1}`,
                content: contentMatch ? contentMatch[1] : `这是第${index + 1}个场景的内容。`,
                description: `场景${index + 1}的描述`,
                dialogues: [],
                background: '',
                mood: '紧张',
                timeOfDay: '下午',
                location: '未知地点'
              }
            } catch (error) {
              console.warn('⚠️ 场景解析失败:', error.message)
              return {
                id: `scene-1-${index + 1}`,
                title: `场景${index + 1}`,
                content: `这是第${index + 1}个场景的内容。`,
                description: `场景${index + 1}的描述`,
                dialogues: [],
                background: '',
                mood: '紧张',
                timeOfDay: '下午',
                location: '未知地点'
              }
            }
          })
          
          return { scenes }
        }
      }
      
      // 如果无法提取scenes，返回空数据
      return { scenes: [] }
    } catch (error) {
      console.error('❌ 部分数据提取失败:', error.message)
      return { scenes: [] }
    }
  }

  // 生成场景详细内容
  static async generateSceneContent(scene, characters, style) {
    const characterNames = characters ? characters.map(c => c.name).join('、') : '主角'
    
    const prompt = `请为以下场景生成详细的互动内容：

场景标题：${scene.title}
场景描述：${scene.description || '无'}
场景类型：${scene.type || '普通场景'}
风格：${style}
角色：${characterNames}

要求：
1. 生成500-800字的详细场景内容，包含环境描述、角色互动、情感氛围
2. 如果场景有选择分支，为每个选择生成详细的后果描述
3. 生成2-3段角色对话，体现角色性格
4. 包含丰富的感官细节（视觉、听觉、触觉等）
5. 营造沉浸式的氛围

请以JSON格式返回，包含以下字段：
- content: 详细场景内容
- description: 场景描述
- dialogues: 对话数组
- background: 背景描述
- mood: 情绪氛围
- timeOfDay: 时间
- location: 地点`

    try {
      const response = await this.callLanguageModel(prompt, {
        temperature: 0.8,
        maxTokens: 2048
      })

      // 尝试解析JSON响应
      const jsonMatch = response.match(/\{[\s\S]*\}/)
      if (jsonMatch) {
        const sceneData = JSON.parse(jsonMatch[0])
        return {
          ...scene,
          content: sceneData.content || scene.content || `在${scene.title}中，${characterNames}面临着重要的选择。`,
          description: sceneData.description || scene.description || `这是${scene.title}的详细描述。`,
          dialogues: sceneData.dialogues || scene.dialogues || [],
          background: sceneData.background || scene.background || `在${scene.title}的背景中，环境氛围紧张而充满悬念。`,
          mood: sceneData.mood || scene.mood || '紧张',
          timeOfDay: sceneData.timeOfDay || scene.timeOfDay || '下午',
          location: sceneData.location || scene.location || `在${scene.title}的场景中`
        }
      } else {
        // 如果无法解析JSON，使用文本内容
        return {
          ...scene,
          content: response || scene.content || `在${scene.title}中，${characterNames}面临着重要的选择。`,
          description: scene.description || `这是${scene.title}的详细描述。`,
          dialogues: scene.dialogues || [],
          background: scene.background || `在${scene.title}的背景中，环境氛围紧张而充满悬念。`,
          mood: scene.mood || '紧张',
          timeOfDay: scene.timeOfDay || '下午',
          location: scene.location || `在${scene.title}的场景中`
        }
      }
    } catch (error) {
      console.error('生成场景内容失败:', error)
      // 返回基础场景数据
      return {
        ...scene,
        content: scene.content || `在${scene.title}中，${characterNames}面临着重要的选择。`,
        description: scene.description || `这是${scene.title}的详细描述。`,
        dialogues: scene.dialogues || [],
        background: scene.background || `在${scene.title}的背景中，环境氛围紧张而充满悬念。`,
        mood: scene.mood || '紧张',
        timeOfDay: scene.timeOfDay || '下午',
        location: scene.location || `在${scene.title}的场景中`
      }
    }
  }

  // 修复常见的JSON错误
  static fixJsonErrors(jsonText) {
    try {
      // 修复常见的JSON错误
      let fixed = jsonText
      
      // 1. 修复末尾多余的逗号
      fixed = fixed.replace(/,(\s*[}\]])/g, '$1')
      
      // 2. 修复单引号
      fixed = fixed.replace(/'/g, '"')
      
      // 3. 修复未转义的引号
      fixed = fixed.replace(/([^\\])"([^"]*)"([^\\])/g, '$1\\"$2\\"$3')
      
      // 4. 修复缺少的引号
      fixed = fixed.replace(/(\w+):/g, '"$1":')
      
      // 5. 修复换行符
      fixed = fixed.replace(/\n/g, '\\n')
      
      return fixed
    } catch (error) {
      console.error('修复JSON错误失败:', error)
      return null
    }
  }

  // 辅助方法：提取标题
  static extractTitle(text) {
    const titleMatch = text.match(/标题[：:]\s*(.+)/i) || text.match(/title[：:]\s*(.+)/i)
    return titleMatch ? titleMatch[1].trim() : "AI生成的故事"
  }

  // 辅助方法：提取摘要
  static extractSummary(text) {
    const summaryMatch = text.match(/摘要[：:]\s*(.+?)(?=章节|角色|$)/is)
    return summaryMatch ? summaryMatch[1].trim() : text.substring(0, 200) + "..."
  }

  // 辅助方法：提取章节
  static extractChapters(text) {
    const chapterMatches = text.match(/章节[：:]\s*(.+?)(?=角色|$)/is)
    if (chapterMatches) {
      const chapters = chapterMatches[1].split('\n').filter(line => line.trim())
      return chapters.map((chapter, index) => ({
        id: index + 1,
        title: chapter.trim(),
        description: "章节描述",
        scenes: 3
      }))
    }
    return [{ id: 1, title: "第一章", description: "故事开始", scenes: 3 }]
  }

  // 辅助方法：提取角色
  static extractCharacters(text) {
    const characterMatches = text.match(/角色[：:]\s*(.+?)(?=剧情|$)/is)
    if (characterMatches) {
      const characters = characterMatches[1].split('\n').filter(line => line.trim())
      return characters.map((character, index) => ({
        name: character.trim(),
        role: index === 0 ? "protagonist" : "supporting",
        description: "角色描述"
      }))
    }
    return [{ name: "主角", role: "protagonist", description: "故事的主要角色" }]
  }

  // 辅助方法：提取主要剧情
  static extractMainPlot(text) {
    const plotMatch = text.match(/剧情[：:]\s*(.+?)(?=分支|$)/is)
    return plotMatch ? plotMatch[1].trim() : text.substring(0, 300) + "..."
  }

  // 辅助方法：提取分支
  static extractBranches(text) {
    const branchMatches = text.match(/分支[：:]\s*(.+?)$/is)
    if (branchMatches) {
      const branches = branchMatches[1].split('\n').filter(line => line.trim())
      return branches.map((branch, index) => ({
        type: "choice",
        description: branch.trim(),
        outcomes: ["选项A", "选项B"]
      }))
    }
    return []
  }

  // 生成故事结构
  static async generateStoryStructure(outline) {
    const prompt = `基于故事大纲创建互动故事结构：

标题：${outline.title}
摘要：${outline.summary}
主要剧情：${outline.mainPlot}

要求：
1. 创建8个场景，分为3个章节（3+3+2）
2. 场景ID格式：scene-{章节}-{场景号}
3. 创建2个分支选择点，每个有2个选项
4. 每个选择包含targetNodeId字段
5. 为每个章节生成详细的描述，包含核心主题、情感氛围和主要事件
6. 每个章节描述应该体现该章节在整个故事中的作用和意义

**重要：请严格按照以下格式输出，使用明确的标记来标识JSON内容：**

===JSON_START===
{
  "chapters": [
    {
      "id": 1,
      "title": "第一章标题",
      "description": "第一章的详细描述，包含本章的核心主题、情感氛围和主要事件",
      "coreEmotion": "紧张、悬疑",
      "scenes": [
        {
          "id": "scene-1-1",
          "title": "场景标题",
          "description": "场景描述",
          "content": "场景内容",
          "dialogues": [{"character": "角色名", "content": "对话内容"}],
          "choices": [
            {
              "id": "choice-1-1-1",
              "text": "选择文本",
              "targetNodeId": "scene-2-1",
              "path": "brave"
            }
          ],
          "imagePrompt": "图像提示词"
        }
      ]
    }
  ]
}
===JSON_END===

请确保：
1. JSON内容完全在 ===JSON_START=== 和 ===JSON_END=== 之间
2. JSON格式完全正确，包含所有必需字段
3. 中文字符不需要特殊处理，直接输出即可`

    const response = await this.callLanguageModel(prompt, {
      temperature: 0.7,
      maxTokens: 4096
    })

    try {
      // 使用标记直接截取JSON内容
      console.log('🔍 AI返回的原始响应:', response.substring(0, 500) + '...')
      
      let jsonText = null
      
      // 方法1: 使用标记直接截取JSON内容
      const startMarker = '===JSON_START==='
      const endMarker = '===JSON_END==='
      
      const startIndex = response.indexOf(startMarker)
      const endIndex = response.indexOf(endMarker)
      
      if (startIndex !== -1 && endIndex !== -1 && endIndex > startIndex) {
        jsonText = response.substring(startIndex + startMarker.length, endIndex).trim()
        console.log('✅ 使用标记成功提取JSON内容')
        console.log('🔍 提取的JSON字符串长度:', jsonText.length)
        console.log('🔍 提取的JSON字符串前200字符:', jsonText.substring(0, 200))
      } else {
        console.warn('⚠️ 未找到JSON标记，尝试传统方法')
        
        // 方法2: 查找第一个完整的JSON对象（备用方案）
        const jsonMatch = response.match(/\{[\s\S]*?\}(?=\s*$|\s*\{|\s*```)/)
        if (jsonMatch) {
          jsonText = jsonMatch[0]
          console.log('🔍 使用传统方法提取JSON')
        }
        
        // 方法3: 查找```json代码块（备用方案）
        if (!jsonText) {
          const codeBlockMatch = response.match(/```json\s*([\s\S]*?)\s*```/)
          if (codeBlockMatch) {
            jsonText = codeBlockMatch[1]
            console.log('🔍 使用代码块方法提取JSON')
          }
        }
      }
      
      if (jsonText) {
        console.log('📝 提取的JSON文本:', jsonText.substring(0, 200) + '...')
        try {
          // 直接解析JSON，不做复杂处理
          const structure = JSON.parse(jsonText)
          console.log('✅ 故事结构JSON解析成功')
          return this.formatStoryStructure(structure, outline)
        } catch (parseError) {
          console.error('❌ JSON解析失败:', parseError.message)
          console.log('⚠️ 使用备用结构')
          return this.createFallbackStructure(outline)
        }
      } else {
        console.log('⚠️ 未找到有效的JSON格式，使用备用结构')
        return this.createFallbackStructure(outline)
      }
    } catch (error) {
      console.error('❌ 解析故事结构JSON失败:', error)
      console.log('📄 原始响应内容:', response)
      return this.createFallbackStructure(outline)
    }
  }

  // 格式化故事结构
  static formatStoryStructure(structure, outline) {
    const formattedStructure = {
      id: Date.now().toString(),
      title: outline.title,
      chapters: [],
      branches: [],
      connections: []
    }

    // 处理章节和场景
    if (structure.chapters && Array.isArray(structure.chapters)) {
      structure.chapters.forEach((chapter, chapterIndex) => {
        const formattedChapter = {
          id: chapterIndex + 1,
          title: chapter.title || `第${chapterIndex + 1}章`,
          description: chapter.description || '',
          scenes: []
        }

        if (chapter.scenes && Array.isArray(chapter.scenes)) {
          chapter.scenes.forEach((scene, sceneIndex) => {
            const sceneId = `scene-${chapterIndex + 1}-${sceneIndex + 1}`
            
            // 处理选择，确保有正确的targetNodeId
            const formattedChoices = (scene.choices || []).map((choice, choiceIndex) => ({
              id: choice.id || `choice-${chapterIndex + 1}-${sceneIndex + 1}-${choiceIndex + 1}`,
              text: choice.text || `选择${choiceIndex + 1}`,
              targetNodeId: choice.targetNodeId || this.findTargetSceneId(choice, chapterIndex + 1, sceneIndex + 1),
              path: choice.path || `path-${choiceIndex + 1}`,
              consequence: choice.consequence || `选择了"${choice.text || `选择${choiceIndex + 1}`}"`
            }))
            
            const formattedScene = {
              id: sceneId,
              title: scene.title || `场景${sceneIndex + 1}`,
              description: scene.description || '',
              content: scene.content || '',
              dialogues: scene.dialogues || [],
              choices: formattedChoices,
              imagePrompt: scene.imagePrompt || '',
              sceneImage: `https://picsum.photos/400/300?random=${chapterIndex * 10 + sceneIndex + 1}`,
              chapter: chapterIndex + 1,
              scene: sceneIndex + 1,
              location: scene.location || '',
              mood: scene.mood || '',
              timeOfDay: scene.timeOfDay || '',
              background: scene.background || ''
            }
            formattedChapter.scenes.push(formattedScene)
          })
        }

        formattedStructure.chapters.push(formattedChapter)
      })
    }

    return formattedStructure
  }

  // 查找目标场景ID
  static findTargetSceneId(choice, currentChapter, currentScene) {
    // 如果选择中有明确的目标信息，使用它
    if (choice.targetChapter && choice.targetScene) {
      return `scene-${choice.targetChapter}-${choice.targetScene}`
    }
    
    // 根据选择类型推断目标场景
    if (choice.text && choice.text.includes('勇敢')) {
      return `scene-${currentChapter + 1}-1` // 勇敢路径
    } else if (choice.text && choice.text.includes('谨慎')) {
      return `scene-${currentChapter + 1}-2` // 谨慎路径
    } else if (choice.text && choice.text.includes('正义')) {
      return `scene-${currentChapter + 1}-1` // 正义结局
    } else if (choice.text && choice.text.includes('和解')) {
      return `scene-${currentChapter + 1}-2` // 和解结局
    }
    
    // 默认：下一个场景
    return `scene-${currentChapter}-${currentScene + 1}`
  }

  // 创建备用结构
  static createFallbackStructure(outline) {
    return {
      id: Date.now().toString(),
      title: outline.title,
      chapters: [
        {
          id: 1,
          title: outline.chapters?.[0]?.title || "故事开端",
          description: outline.chapters?.[0]?.description || "故事的开端，建立世界观和角色关系",
          scenes: [
            {
              id: "scene-1-1",
              title: outline.chapters?.[0]?.scenes?.[0]?.title || "故事开始",
              description: outline.chapters?.[0]?.scenes?.[0]?.description || "故事开始",
              content: outline.chapters?.[0]?.scenes?.[0]?.content || `基于你的创意"${outline.title}"，故事开始了。`,
              dialogues: [
                { character: "主角", content: "这是故事的开始..." }
              ],
              choices: [],
              imagePrompt: `${outline.title}, story beginning, ${outline.genre || 'fantasy'} style`,
              sceneImage: "https://picsum.photos/400/300?random=1",
              chapter: 1,
              scene: 1,
              location: outline.chapters?.[0]?.scenes?.[0]?.location || "开始地点",
              mood: outline.chapters?.[0]?.scenes?.[0]?.mood || "神秘",
              timeOfDay: "白天",
              background: outline.chapters?.[0]?.scenes?.[0]?.background || "故事背景"
            },
            {
              id: "scene-1-2",
              title: outline.chapters?.[0]?.scenes?.[1]?.title || "重要发现",
              description: outline.chapters?.[0]?.scenes?.[1]?.description || "发现改变命运的关键线索",
              content: outline.chapters?.[0]?.scenes?.[1]?.content || `在"${outline.title}"的世界中，你发现了一个重要的线索。`,
              dialogues: [
                { character: "主角", content: "这很重要..." }
              ],
              choices: [],
              imagePrompt: `${outline.title}, important discovery, ${outline.genre || 'fantasy'} style`,
              sceneImage: "https://picsum.photos/400/300?random=2",
              chapter: 1,
              scene: 2,
              location: outline.chapters?.[0]?.scenes?.[1]?.location || "发现地点",
              mood: outline.chapters?.[0]?.scenes?.[1]?.mood || "好奇与紧张",
              timeOfDay: "黄昏",
              background: outline.chapters?.[0]?.scenes?.[1]?.background || "重要线索散发着神秘的光芒"
            },
            {
              id: "scene-1-3",
              title: outline.chapters?.[0]?.scenes?.[2]?.title || "第一个选择",
              description: outline.chapters?.[0]?.scenes?.[2]?.description || "面临第一个重要的选择",
              content: outline.chapters?.[0]?.scenes?.[2]?.content || `在"${outline.title}"的故事中，你面临第一个重要选择。`,
              dialogues: [
                { character: "引导者", content: "现在你必须选择：接受使命还是回到平凡的生活？" }
              ],
              choices: [
                {
                  id: "choice-1-3-1",
                  text: "勇敢面对",
                  targetNodeId: "scene-2-1",
                  path: "brave",
                  consequence: "你选择了勇敢面对挑战，开始了英雄之路"
                },
                {
                  id: "choice-1-3-2",
                  text: "谨慎行事",
                  targetNodeId: "scene-2-2",
                  path: "cautious",
                  consequence: "你选择了谨慎行事，先了解更多信息"
                }
              ],
              imagePrompt: `${outline.title}, important choice, crossroads, ${outline.genre || 'fantasy'} style`,
              sceneImage: "https://picsum.photos/400/300?random=3",
              chapter: 1,
              scene: 3,
              location: "选择之地",
              mood: "紧张与期待",
              timeOfDay: "深夜",
              background: "命运的分叉路口，选择将决定未来的道路"
            }
          ]
        },
        {
          id: 2,
          title: outline.chapters?.[1]?.title || "冲突与选择",
          description: outline.chapters?.[1]?.description || "主要冲突的展开，分支选择点",
          scenes: [
            {
              id: "scene-2-1",
              title: outline.chapters?.[1]?.scenes?.[0]?.title || "勇敢之路",
              description: outline.chapters?.[1]?.scenes?.[0]?.description || "选择勇敢面对挑战后的发展",
              content: outline.chapters?.[1]?.scenes?.[0]?.content || `在"${outline.title}"的故事中，你选择了勇敢面对挑战。`,
              dialogues: [
                { character: "主角", content: "我不会退缩，无论面对什么困难。" }
              ],
              choices: [
                {
                  id: "choice-2-1-1",
                  text: "继续勇敢前行",
                  targetNodeId: "scene-2-3",
                  path: "brave-continue",
                  consequence: "你选择继续勇敢前行，来到汇聚点"
                }
              ],
              imagePrompt: `${outline.title}, brave hero, facing challenges, ${outline.genre || 'fantasy'} style`,
              sceneImage: "https://picsum.photos/400/300?random=4",
              chapter: 2,
              scene: 1,
              location: outline.chapters?.[1]?.scenes?.[0]?.location || "挑战之地",
              mood: outline.chapters?.[1]?.scenes?.[0]?.mood || "勇敢与紧张",
              timeOfDay: "白天",
              background: outline.chapters?.[1]?.scenes?.[0]?.background || "充满挑战的环境，英雄的试炼场"
            },
            {
              id: "scene-2-2",
              title: outline.chapters?.[1]?.scenes?.[1]?.title || "谨慎的观察",
              description: outline.chapters?.[1]?.scenes?.[1]?.description || "选择谨慎行事后的发展",
              content: outline.chapters?.[1]?.scenes?.[1]?.content || `在"${outline.title}"的故事中，你选择了谨慎行事。`,
              dialogues: [
                { character: "主角", content: "我需要了解更多信息才能做出正确的决定。" }
              ],
              choices: [
                {
                  id: "choice-2-2-1",
                  text: "坚持原则",
                  targetNodeId: "scene-3-1",
                  path: "justice",
                  consequence: "你选择了坚持正义，即使面临困难"
                },
                {
                  id: "choice-2-2-2",
                  text: "寻求和解",
                  targetNodeId: "scene-3-2",
                  path: "peace",
                  consequence: "你选择了和解，寻求和平的解决方案"
                }
              ],
              imagePrompt: `${outline.title}, careful observation, strategic thinking, ${outline.genre || 'fantasy'} style`,
              sceneImage: "https://picsum.photos/400/300?random=5",
              chapter: 2,
              scene: 2,
              location: outline.chapters?.[1]?.scenes?.[1]?.location || "观察点",
              mood: outline.chapters?.[1]?.scenes?.[1]?.mood || "谨慎与思考",
              timeOfDay: "黄昏",
              background: outline.chapters?.[1]?.scenes?.[1]?.background || "观察和思考的地方，策略的制定地"
            },
            {
              id: "scene-2-3",
              title: outline.chapters?.[1]?.scenes?.[2]?.title || "汇聚点",
              description: outline.chapters?.[1]?.scenes?.[2]?.description || "不同路径的汇聚",
              content: outline.chapters?.[1]?.scenes?.[2]?.content || `在"${outline.title}"的故事中，无论你选择了什么路径，最终都会来到这里。`,
              dialogues: [
                { character: "主角", content: "看来所有的选择都指向了这里。" }
              ],
              choices: [
                {
                  id: "choice-2-3-1",
                  text: "坚持正义",
                  targetNodeId: "scene-3-1",
                  path: "justice",
                  consequence: "你选择了坚持正义，走向正义的胜利"
                },
                {
                  id: "choice-2-3-2",
                  text: "寻求和解",
                  targetNodeId: "scene-3-2",
                  path: "peace",
                  consequence: "你选择了寻求和解，走向和平的结局"
                }
              ],
              imagePrompt: `${outline.title}, convergence point, multiple paths meeting, ${outline.genre || 'fantasy'} style`,
              sceneImage: "https://picsum.photos/400/300?random=6",
              chapter: 2,
              scene: 3,
              location: outline.chapters?.[1]?.scenes?.[2]?.location || "命运交汇点",
              mood: outline.chapters?.[1]?.scenes?.[2]?.mood || "庄严与神秘",
              timeOfDay: "夜晚",
              background: outline.chapters?.[1]?.scenes?.[2]?.background || "所有道路的终点，命运的汇聚地"
            }
          ]
        },
        {
          id: 3,
          title: outline.chapters?.[2]?.title || "结局",
          description: outline.chapters?.[2]?.description || "故事的高潮部分，通向不同的结局",
          scenes: [
            {
              id: "scene-3-1",
              title: outline.chapters?.[2]?.scenes?.[0]?.title || "正义的胜利",
              description: outline.chapters?.[2]?.scenes?.[0]?.description || "坚持原则的结局",
              content: outline.chapters?.[2]?.scenes?.[0]?.content || `在"${outline.title}"的故事中，你选择了坚持原则，最终取得了正义的胜利。`,
              dialogues: [
                { character: "主角", content: "正义最终会战胜邪恶！" }
              ],
              choices: [],
              imagePrompt: `${outline.title}, justice victory, moral triumph, ${outline.genre || 'fantasy'} style`,
              sceneImage: "https://picsum.photos/400/300?random=7",
              chapter: 3,
              scene: 1,
              location: outline.chapters?.[2]?.scenes?.[0]?.location || "正义殿堂",
              mood: outline.chapters?.[2]?.scenes?.[0]?.mood || "胜利与正义",
              timeOfDay: "黎明",
              background: outline.chapters?.[2]?.scenes?.[0]?.background || "正义的曙光，胜利的见证"
            },
            {
              id: "scene-3-2",
              title: outline.chapters?.[2]?.scenes?.[1]?.title || "和平的结局",
              description: outline.chapters?.[2]?.scenes?.[1]?.description || "寻求和解的结局",
              content: outline.chapters?.[2]?.scenes?.[1]?.content || `在"${outline.title}"的故事中，你选择了寻求和解，最终实现了和平。`,
              dialogues: [
                { character: "主角", content: "和平比胜利更重要。" }
              ],
              choices: [],
              imagePrompt: `${outline.title}, peace ending, reconciliation, ${outline.genre || 'fantasy'} style`,
              sceneImage: "https://picsum.photos/400/300?random=8",
              chapter: 3,
              scene: 2,
              location: outline.chapters?.[2]?.scenes?.[1]?.location || "和平圣地",
              mood: outline.chapters?.[2]?.scenes?.[1]?.mood || "和平与和谐",
              timeOfDay: "黄昏",
              background: outline.chapters?.[2]?.scenes?.[1]?.background || "和平的象征，和谐的见证"
            }
          ]
        }
      ],
      branches: [],
      connections: []
    }
  }

  // 生成角色详细信息
  static async generateCharacterInfo(characterList, content, genre) {
    const prompt = `为故事创建角色档案：

故事：${content}
类型：${genre}
角色：${characterList ? characterList.map(c => c.name || c).join(', ') : '主角、导师、对手'}

要求：
1. 创建3-4个主要角色
2. 每个角色包含：名称、角色定位、外观、背景、性格、动机、弱点
3. 角色要有深度和成长空间
4. 符合${genre}题材特点

请以JSON格式返回：
{
  "characters": [
    {
      "id": "char-1",
      "name": "角色名",
      "role": "protagonist",
      "description": "角色描述",
      "appearance": "外观描述",
      "background": "背景故事",
      "personality": "性格特征",
      "motivation": "动机目标",
      "weakness": "弱点缺陷",
      "relationships": "与其他角色的关系",
      "imagePrompt": "角色形象提示词",
      "languageStyle": "语言风格"
    }
  ]
}`

    const response = await this.callLanguageModel(prompt, {
      temperature: 0.7,
      maxTokens: 2048
    })

    try {
      console.log('🔍 AI返回的角色响应长度:', response.length)
      
      // 尝试多种JSON提取方法
      let jsonText = null
      
      // 方法1: 查找```json代码块
      const jsonBlockMatch = response.match(/```json\s*([\s\S]*?)\s*```/)
      if (jsonBlockMatch) {
        jsonText = jsonBlockMatch[1]
        console.log('✅ 找到JSON代码块')
      }
      
      // 方法2: 查找```代码块
      if (!jsonText) {
        const codeBlockMatch = response.match(/```\s*([\s\S]*?)\s*```/)
        if (codeBlockMatch) {
          jsonText = codeBlockMatch[1]
          console.log('✅ 找到代码块')
        }
      }
      
      // 方法3: 查找JSON对象
      if (!jsonText) {
        const jsonMatch = response.match(/\{[\s\S]*\}/)
        if (jsonMatch) {
          jsonText = jsonMatch[0]
          console.log('✅ 找到JSON对象')
        }
      }
      
      if (jsonText) {
        console.log('📝 提取的JSON文本长度:', jsonText.length)
        // 使用高级JSON修复工具
        const data = AdvancedJsonRepair.repairAndParse(jsonText)
        if (data && data.characters) {
          console.log('✅ 角色JSON解析成功，角色数:', data.characters.length)
          return this.formatCharacters(data.characters)
        } else {
          console.log('⚠️ 高级JSON修复失败，尝试原始方法')
          // 尝试原始方法
          try {
            const fixedJson = this.fixJsonErrors(jsonText)
            const data = JSON.parse(fixedJson)
            console.log('✅ 角色JSON解析成功，角色数:', data.characters?.length || 0)
            return this.formatCharacters(data.characters || [])
          } catch (error) {
            console.error('❌ 原始JSON解析也失败:', error)
            return this.createFallbackCharacters(content, genre)
          }
        }
      } else {
        console.log('⚠️ 未找到有效的角色JSON格式，使用备用角色')
        return this.createFallbackCharacters(content, genre)
      }
    } catch (error) {
      console.error('❌ 解析角色信息JSON失败:', error.message)
      console.log('📄 原始响应前500字符:', response.substring(0, 500))
      return this.createFallbackCharacters(content, genre)
    }
  }

  // 格式化角色信息
  static formatCharacters(characters) {
    console.log('🔄 格式化角色信息，原始角色数:', characters.length)
    
    const formattedCharacters = characters.map((char, index) => {
      const formattedChar = {
        id: char.id || `char-${index + 1}`,
        name: char.name || `角色${index + 1}`,
        role: char.role || 'supporting',
        description: char.description || `这是${char.name || `角色${index + 1}`}的详细描述`,
        appearance: char.appearance || '神秘而引人注目',
        background: char.background || `与主角命运交织的角色`,
        personality: char.personality || '复杂而多面',
        motivation: char.motivation || '追求自己的目标',
        weakness: char.weakness || '有隐藏的脆弱一面',
        relationships: char.relationships || '与主角有重要联系',
        languageStyle: char.languageStyle || '普通',
        stats: char.stats || { 
          intelligence: 70 + Math.floor(Math.random() * 20), 
          strength: 60 + Math.floor(Math.random() * 20), 
          charisma: 70 + Math.floor(Math.random() * 20), 
          wisdom: 65 + Math.floor(Math.random() * 20) 
        },
        imagePrompt: char.imagePrompt || `${char.name || `character${index + 1}`} from story`,
        imageUrl: `https://picsum.photos/200/200?random=${10 + index}`,
        isActive: true,
        firstAppearance: '故事开始',
        lastAppearance: '故事结束',
        dialogueCount: 0
      }
      
      console.log(`✅ 格式化角色 ${index + 1}:`, formattedChar.name)
      return formattedChar
    })
    
    console.log('✅ 角色格式化完成，最终角色数:', formattedCharacters.length)
    return formattedCharacters
  }

  // 创建备用角色
  static createFallbackCharacters(content, genre) {
    console.log('🔄 创建备用角色，基于内容:', content.substring(0, 50) + '...')
    
    const characters = [
      {
        id: "char-1",
        name: "主角",
        role: "protagonist",
        description: `基于"${content}"故事的主角，玩家将扮演这个角色`,
        appearance: "年轻有为，眼神坚定，穿着现代服装但透露出不凡的气质",
        background: `在"${content}"的故事中，一个普通的年轻人，突然被命运选中成为英雄`,
        personality: "勇敢、正义、有同情心，但有时会犹豫不决",
        motivation: "渴望保护重要的人，追求正义和真理",
        weakness: "有时过于理想主义，容易相信他人",
        relationships: "与其他角色之间的关系将在故事发展中逐渐揭示",
        languageStyle: "普通",
        stats: { intelligence: 75, strength: 70, charisma: 80, wisdom: 65 },
        imagePrompt: `protagonist from "${content}", determined expression, heroic pose, ${genre} style`,
        imageUrl: "https://picsum.photos/200/200?random=10",
        isActive: true,
        firstAppearance: '故事开始',
        lastAppearance: '故事结束',
        dialogueCount: 0
      },
      {
        id: "char-2",
        name: "引导者",
        role: "mentor",
        description: `在"${content}"故事中引导主角的神秘人物`,
        appearance: "智慧的长者，眼神深邃，穿着古朴的服装",
        background: "拥有丰富经验的导师，了解故事的真相",
        personality: "智慧、耐心、神秘，但有时会隐瞒重要信息",
        motivation: "希望主角能够成长，承担起拯救世界的责任",
        weakness: "过去的创伤让他对某些事情保持沉默",
        relationships: "主角的导师和指引者，但关系复杂",
        languageStyle: "神秘",
        stats: { intelligence: 90, strength: 40, charisma: 70, wisdom: 95 },
        imagePrompt: `wise mentor from "${content}", mysterious aura, ancient wisdom, ${genre} style`,
        imageUrl: "https://picsum.photos/200/200?random=11",
        isActive: true,
        firstAppearance: '故事开始',
        lastAppearance: '故事中段',
        dialogueCount: 0
      },
      {
        id: "char-3",
        name: "对手",
        role: "antagonist",
        description: `"${content}"故事中的主要对手`,
        appearance: "强大而危险，眼神冷酷，穿着暗色服装",
        background: "拥有强大力量的敌人，与主角形成对立",
        personality: "冷酷、狡猾、强大，但可能有自己的理由",
        motivation: "追求权力和控制，认为自己的方法是正确的",
        weakness: "过于自信，低估了主角的潜力",
        relationships: "主角的主要对手，但可能有复杂的过去",
        languageStyle: "冷酷",
        stats: { intelligence: 80, strength: 90, charisma: 60, wisdom: 70 },
        imagePrompt: `antagonist from "${content}", powerful and dangerous, dark aura, ${genre} style`,
        imageUrl: "https://picsum.photos/200/200?random=12",
        isActive: true,
        firstAppearance: '故事中段',
        lastAppearance: '故事结束',
        dialogueCount: 0
      }
    ]
    
    console.log('✅ 备用角色创建完成，角色数:', characters.length)
    return characters
  }
}
